import { useMemo, useState } from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    horizontalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowRight, GripVertical, MessageSquarePlus, Sparkles, Trash2 } from 'lucide-react';
import type { UtilityTemplateGroup } from '@/services/api/broadcast';
import type { MultiTemplateSelectionEntry } from '../types';
import MessengerPreview from '@/pages/Meta/Broadcast/components/MessengerPreview';

interface TemplateColumnsCanvasProps {
    /** Já ordenada por prioridade (esquerda = maior prioridade). */
    selection: MultiTemplateSelectionEntry[];
    templatesById: Map<number, UtilityTemplateGroup>;
    onReorder: (next: MultiTemplateSelectionEntry[]) => void;
    onRemove: (groupId: number) => void;
    onSecondaryChange: (groupId: number, secondaryGroupId: number | null) => void;
}

/** Picker inline do auxiliar de texto de UMA coluna — adaptado do padrão de
 * seleção de "template complementar" único do wizard 1+1 (StepSelectPage.tsx),
 * agora escopado por template principal em vez de global à esteira. */
function SecondaryPicker({
    options,
    onChange,
    onClose,
}: {
    options: UtilityTemplateGroup[];
    onChange: (secondaryGroupId: number | null) => void;
    onClose: () => void;
}) {
    return (
        <div className="space-y-1.5 rounded-xl border border-gray-200 bg-gray-50 p-2">
            {options.length === 0 ? (
                <p className="text-xs text-gray-400 py-2 text-center">Nenhum template disponível para auxiliar.</p>
            ) : (
                <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
                    {options.map(group => (
                        <button
                            key={group.id}
                            type="button"
                            onClick={() => {
                                onChange(group.id);
                                onClose();
                            }}
                            className="w-full flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm hover:border-[#7dd3fc] hover:bg-[#7dd3fc]/5 transition-colors"
                        >
                            <Sparkles className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                            <span className="truncate font-medium text-gray-800">{group.template_name}</span>
                        </button>
                    ))}
                </div>
            )}
            <button
                type="button"
                onClick={onClose}
                className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-1.5"
            >
                Cancelar
            </button>
        </div>
    );
}

/** Uma coluna do canvas: card arrastável com preview real do template principal
 * (+ auxiliar, quando definido) em cima, e o seletor de auxiliar embaixo. */
function TemplateColumn({
    entry,
    index,
    total,
    isLast,
    group,
    secondaryOptions,
    secondaryGroup,
    onRemove,
    onSecondaryChange,
}: {
    entry: MultiTemplateSelectionEntry;
    index: number;
    total: number;
    isLast: boolean;
    group?: UtilityTemplateGroup;
    secondaryOptions: UtilityTemplateGroup[];
    secondaryGroup?: UtilityTemplateGroup;
    onRemove: (groupId: number) => void;
    onSecondaryChange: (groupId: number, secondaryGroupId: number | null) => void;
}) {
    const [pickingSecondary, setPickingSecondary] = useState(false);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: entry.groupId,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    const isTopPriority = index === 0 && total > 1;

    return (
        <div className="flex items-stretch">
            <div
                ref={setNodeRef}
                style={style}
                className={`flex w-80 shrink-0 flex-col rounded-2xl border bg-white overflow-hidden transition-shadow ${
                    isDragging ? 'shadow-xl ring-2 ring-[#7dd3fc]' : 'border-gray-200 shadow-sm hover:shadow-md'
                }`}
            >
                {/* Cabeçalho: prioridade com legenda clara, nome, drag handle, remover */}
                <div className="flex items-center gap-2.5 border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white px-3.5 py-3">
                    <button
                        type="button"
                        {...attributes}
                        {...listeners}
                        className="p-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none rounded hover:bg-gray-100 transition-colors"
                        aria-label="Arrastar para reordenar"
                    >
                        <GripVertical className="w-4 h-4" />
                    </button>
                    <div
                        className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center shrink-0 ${
                            isTopPriority ? 'bg-[#0f172a] text-white' : 'bg-gray-100 text-gray-500'
                        }`}
                    >
                        {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate leading-tight">
                            {entry.templateName}
                        </p>
                        {total > 1 && (
                            <p className="text-[11px] text-gray-400 leading-tight">
                                {isTopPriority ? 'Vence em caso de conflito' : `Prioridade ${index + 1} de ${total}`}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => onRemove(entry.groupId)}
                        className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 shrink-0 transition-colors"
                        aria-label="Remover template"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>

                {/* Preview real do template principal + auxiliar (quando
                    definido) — mesma sequência que o lead recebe de verdade,
                    principal primeiro, auxiliar em seguida. */}
                <div className="p-3 bg-gray-50/60">
                    {group ? (
                        <div className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
                            <MessengerPreview
                                blocks={[]}
                                utilityTemplate={{
                                    title: group.title,
                                    subtitle: group.subtitle,
                                    template_name: group.template_name,
                                    image_url: group.image_url,
                                    thumbnail_url: group.thumbnail_url,
                                    buttons: group.buttons,
                                }}
                                secondaryUtilityTemplate={
                                    secondaryGroup
                                        ? { title: secondaryGroup.title, template_name: secondaryGroup.template_name }
                                        : null
                                }
                            />
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400 text-center py-10">Template não encontrado.</p>
                    )}
                </div>

                {/* Auxiliar de texto vinculado a esta coluna */}
                <div className="border-t border-gray-100 p-3 space-y-2">
                    <div className="flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-gray-400" />
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                            Auxiliar (enviado em seguida)
                        </span>
                    </div>
                    {pickingSecondary ? (
                        <SecondaryPicker
                            options={secondaryOptions}
                            onChange={(id) => onSecondaryChange(entry.groupId, id)}
                            onClose={() => setPickingSecondary(false)}
                        />
                    ) : entry.secondaryGroupId != null ? (
                        <div className="flex items-center justify-between rounded-xl border-2 border-[#7dd3fc] bg-[#7dd3fc]/5 px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <Sparkles className="w-3.5 h-3.5 text-[#0f172a] shrink-0" />
                                <span className="text-sm font-medium text-gray-900 truncate">
                                    {secondaryGroup?.template_name ?? `Template #${entry.secondaryGroupId}`}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => onSecondaryChange(entry.groupId, null)}
                                className="p-1 rounded-lg hover:bg-red-50 shrink-0 transition-colors"
                                aria-label="Remover auxiliar"
                            >
                                <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setPickingSecondary(true)}
                            className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-[#7dd3fc] hover:text-[#0f172a] hover:bg-[#7dd3fc]/5 transition-colors"
                        >
                            <MessageSquarePlus className="w-4 h-4" />
                            Adicionar auxiliar
                        </button>
                    )}
                </div>
            </div>

            {/* Seta conectora indicando ordem/prioridade até a próxima coluna */}
            {!isLast && (
                <div className="flex items-center justify-center w-10 shrink-0">
                    <ArrowRight className="w-5 h-5 text-gray-300" />
                </div>
            )}
        </div>
    );
}

/**
 * Canvas horizontal de colunas arrastáveis — cada template principal selecionado
 * na esteira vira uma coluna com preview real em cima e auxiliar de texto
 * vinculado àquela coluna embaixo. Ordem esquerda->direita = prioridade de
 * resolução de conflito (drag-and-drop horizontal via @dnd-kit).
 * Ver PRD-esteira-multi-template-utility.md, seção 3, "Revisão v3".
 */
export default function TemplateColumnsCanvas({
    selection,
    templatesById,
    onReorder,
    onRemove,
    onSecondaryChange,
}: TemplateColumnsCanvasProps) {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const selectedGroupIds = useMemo(() => new Set(selection.map(s => s.groupId)), [selection]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = selection.findIndex(s => s.groupId === active.id);
        const newIndex = selection.findIndex(s => s.groupId === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const reordered = arrayMove(selection, oldIndex, newIndex);
        onReorder(reordered.map((s, i) => ({ ...s, priority: i })));
    };

    if (selection.length === 0) return null;

    return (
        <div className="space-y-3">
            {selection.length > 1 && (
                <div className="flex items-center gap-2 rounded-lg bg-blue-50/60 border border-blue-100 px-3 py-2">
                    <GripVertical className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                    <p className="text-xs text-blue-700">
                        Arraste as colunas para reordenar. Em caso de sobreposição entre templates, a coluna mais à
                        esquerda vence.
                    </p>
                </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                    items={selection.map(s => s.groupId)}
                    strategy={horizontalListSortingStrategy}
                >
                    <div className="flex items-stretch overflow-x-auto pb-4 pt-1 -mx-1 px-1">
                        {selection.map((entry, idx) => {
                            const secondaryOptions = Array.from(templatesById.values()).filter(
                                g => g.id !== entry.groupId && !selectedGroupIds.has(g.id)
                            );
                            return (
                                <TemplateColumn
                                    key={entry.groupId}
                                    entry={entry}
                                    index={idx}
                                    total={selection.length}
                                    isLast={idx === selection.length - 1}
                                    group={templatesById.get(entry.groupId)}
                                    secondaryOptions={secondaryOptions}
                                    secondaryGroup={
                                        entry.secondaryGroupId != null
                                            ? templatesById.get(entry.secondaryGroupId)
                                            : undefined
                                    }
                                    onRemove={onRemove}
                                    onSecondaryChange={onSecondaryChange}
                                />
                            );
                        })}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
}
